import { Configuration } from 'src/app/models/configurators-2d/applications/fence/configuration';
import { PanelCount } from 'src/app/models/configurators-2d/applications/fence/panel-count';
import { Section } from 'src/app/models/configurators-2d/applications/fence/section';
import { Side } from 'src/app/models/configurators-2d/applications/fence/side';
import { Color } from '../../models/configurators-2d/applications/fence/color';
import { FenceConstants } from 'src/app/models/configurators-2d/applications/fence/fence-constants';

/**
 * Détermination de la hauteur maximale des poteaux de la clôture
 * @param configuration objet configuration
 * @returns hauteur maximum de la cloture
 */
export const getMaxHeight = (configuration: Configuration): number => {
  return configuration.sides.reduce((h, side) => {
    let sideHeight = side.sections.reduce(
      (h2, section) => (Number(section.height) > h2 ? Number(section.height) : h2),
      0
    );
    return sideHeight > h ? sideHeight : h;
  }, 0);
}

/**
 * Fonction permettant de récupérer le width ratio (pour la pose verticale)
 * @param height // le max height trouvé dans les toutes les sections
 * @returns width ratio
 */
export function getWidthRatio(height: number): number {
  switch(height) {
    case FenceConstants.FENCE_HEIGHT_95: return 0.5; // Clôture 95cm
    case FenceConstants.FENCE_HEIGHT_139: return 0.75; // Clôture 139cm
    default: return 1; // Clôture 183cm par défaut
  }
}

/**
 * Fonction de calcul du nombre de décors horizontaux
 * @param configuration objet configuration
 * @returns nombre de panneaux horizontaux
 */
export const getDecorationCount = (configuration: Configuration): number => {
  let decorationCount = 0;

  //Vérification des lames décoratives
  if (configuration.horizontalDecorativeBoards !== undefined) {
    for (let i = 0; i < configuration.horizontalDecorativeBoards.length; i++) {
      decorationCount +=
        configuration.horizontalDecorativeBoards[i].quantity !== undefined
          ? configuration.horizontalDecorativeBoards[i].quantity
          : 0;
    }
  }

  //Vérification des décors
  if (configuration.horizontalDecorations !== undefined) {
    for (let i = 0; i < configuration.horizontalDecorations.length; i++) {
      decorationCount +=
        configuration.horizontalDecorations[i].quantity !== undefined
          ? configuration.horizontalDecorations[i].quantity
          : 0;
    }
  }

  //Vérification des panneaux
  if (configuration.horizontalPanels !== undefined) {
    for (let i = 0; i < configuration.horizontalPanels.length; i++) {
      decorationCount +=
        configuration.horizontalPanels[i].quantity !== undefined ? configuration.horizontalPanels[i].quantity : 0;
    }
  }

  return decorationCount;
}

/**
 * Fonction de détermination du nombre de lames à empiler en fonction de la hauteur de la clôture
 * @returns nombre de lames à empiler
 */
export const getBoardNumber = (configuration: Configuration, boards: Color[]): number => {
  //s'il n'y a pas de poteau alors on ne les remplis pas
  if (!configuration.sides) {
    return 0;
  }

  let heights: number[] = configuration.sides
    .map(s => s.sections.map(s2 => s2.height))
    .reduce((acc, curVal) => acc.concat(curVal), []);

  if (heights.length === 0) {
    return 0;
  }

  let height = Math.max(...heights);

  // Pour la pose verticale, la largeur du panneau ne change pas
  if (configuration.isVertical) {
    height = FenceConstants.FENCE_WIDTH;
  }

  let boardHeight = getConfigurationLongestBoard(configuration, boards)?.height;

  if (!boardHeight || boardHeight <= 0) {
    return 0;
  }

  return Math.trunc(height / boardHeight);
}

/**
 * Check si le nombre de décors n'est pas supérieur au nombre de panneaux complets
 * @param configuration objet configuration
 * @returns boolean
 */
export const checkPanelCount = (configuration: Configuration): boolean => {
  setPanelCount(configuration);
  return getDecorationCount(configuration) <= configuration.panelCount.completeCount;
}

/**
 * Rotation d'une image à 90°
 * @param path URL de l'image
 * @param rotateAngle
 * @param widthRatio
 * @returns image retournée à l'angle indiqué
 */
export const rotateImg = (path: string, rotateAngle: number = 90, widthRatio: number = 1): Promise<string> => {
  const getBase64FromUrl = async (url: string) => {
    const data = await fetch(url);
    const blob = await data.blob();
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        const base64data = reader.result;
        resolve(base64data);
      };
    });
  };

  const rotateBase64Image = (base64data: string) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const image = new Image();
    image.src = base64data;
    return new Promise<string>(resolve => {
      image.onload = () => {
        canvas.width = image.height;
        canvas.height = image.width * widthRatio;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((rotateAngle * Math.PI) / 180);
        ctx.drawImage(image, -image.width / 2, -image.height / 2);
        ctx.restore();

        resolve(canvas.toDataURL());
      };
    });
  };

  return new Promise(resolve => {
    getBase64FromUrl(path).then((res: string) => {
      rotateBase64Image(res).then((res2: string) => {
        resolve(res2);
      });
    });
  });
}

/**
 * Fonction de calcul de la longueur totale de la cloture
 * @param configuration objet configuration
 * @returns longueur totale de la cloture
 */
export const getTotalLength = (configuration: Configuration): number => {
  let length = 0;

  for (let i = 0; i < configuration.sides.length; i++) {
    for (let j = 0; j < configuration.sides[i].sections.length; j++) {
      length += configuration.sides[i].sections[j].length;
    }
  }

  return length;
}

/**
 * Fonction de calcul de la longueur totale d'un côté pouvant contenir une decoration
 * @param side
 * @param decorationWidth
 */
export const getSideLengthForDecoration = (side: Side, decorationWidth: number): number => {
  let length = 0;

  if (side != null) {
    for (const section of side?.sections.filter(s => s.height >= decorationWidth)) {
      length += section.length;
    }
  }

  return length;
}

/**
 * Fonction de détermination si le nombre de décors est égal au nombre total de panneaux
 * @param configuration objet configuration
 * @param type orientation (horizontal ou vertical)
 * @returns boolean
 */
export const isDecorationEqualOrGreaterThanToPanelCount = (configuration: Configuration, type: string): boolean => {
  switch (type) {
    case 'horizontal': {
      if (!configuration.panelCount) {
        setPanelCount(configuration);
      }

      // On a un décor
      return (
        getDecorationCount(configuration) >= configuration.panelCount.completeCount &&
        configuration.panelCount.completeCount > 0
      );
    }
    default:
      return false;
  }
}

/**
 * Fonction de détermination du nombre de panneaux pleins disponibles
 * @returns nombre de panneaux
 */
export const setPanelCount = (configuration: Configuration): void => {
  let result: PanelCount = undefined;

  if (!configuration.postType) {
    return;
  }

  if (configuration.sides) {
    // Calcul du nombre de panneaux par section
    configuration.sides.forEach(s => setSidePanelCount(s.id, configuration));

    // calcul du nombre de panneaux par longueur
    result = {
      completeCount: configuration.sides.reduce((sum, side) => sum + side.panelCount.completeCount, 0),
      remainingCount: configuration.sides.reduce((sum, side) => sum + side.panelCount.remainingCount, 0),
      remainingVerticalBoardsCount: configuration.sides.reduce((sum, side) => sum + side.panelCount.remainingVerticalBoardsCount, 0),
    };
  }

  configuration.panelCount = result;
}

/**
 * Fonction de détermination de la longueur disponible par section
 * @returns longueur de section disponible
 */
export const getSectionLength = (section: Section, verticalDecorsLength: number, configuration: Configuration): number => {
  let sideLength = section.length;

  const doorlength = configuration.postType.gates.length > 0 ? configuration.postType.gates[0].width : 0;
  if (section.hasDoor) {
    sideLength -= doorlength;
  }

  // retire la longueur des décors verticaux
  sideLength -= verticalDecorsLength;

  return sideLength;
}

/**
 * Fonction de détermination du nombre de panneaux pleins disponibles par côté
 * @param sideId l'id du côté
 * @param configuration la configuration
 * @returns nombre de panneaux
 */
export const setSidePanelCount = (sideId: string, configuration: Configuration): void => {
  let result: PanelCount = {
    completeCount: 0,
    remainingCount: 0,
    remainingVerticalBoardsCount: 0
  };
  if (!(configuration.sides && configuration.postType)) {
    return;
  }

  // Récupération du côté
  const indexSide = configuration.sides.findIndex(s => s.id == sideId);
  const side = configuration.sides.find(s => s.id == sideId);

  for (const section of configuration.sides[indexSide].sections) {
    const sectionPanelCount = getSectionPanelCount(section, side, configuration);
    result.completeCount += sectionPanelCount.completeCount;
    result.remainingCount += sectionPanelCount.remainingCount;
    result.remainingVerticalBoardsCount += sectionPanelCount.remainingVerticalBoardsCount;
  }

  configuration.sides[indexSide].panelCount = result;
}

/**
 * Fonction permettant de calculer le nombre de panneaux complet/incomplet par côté
 * @param section
 * @param side
 * @param configuration
 */
export const getSectionPanelCount = (section: Section, side: Side, configuration: Configuration): PanelCount => {
  const boardLength = getConfigurationBoardsMaxLength(configuration);
  const sectionLength = getSectionLength(
    section,
    getVerticalDecorsLengthByConfigurationSideLength(side, section),
    configuration
  );
  const remainingLength = sectionLength % boardLength;
  let remainingVerticalBoardsCount = 0;

  if (configuration.isVertical && remainingLength > 0) {
    const boardHeight = getConfigurationBoardsMaxHeight(configuration);
    remainingVerticalBoardsCount = Math.ceil(remainingLength / boardHeight);
  }

  return {
    completeCount: Math.floor(sectionLength / boardLength),
    remainingCount: remainingLength > 0 ? 1 : 0,
    remainingVerticalBoardsCount: remainingVerticalBoardsCount
  };
}

/**
 * Récupère la lame la plus grande
 * @param configuration
 * @param colors Lames compatibles avec la configuration
 */
const getConfigurationLongestBoard = (configuration: Configuration, colors: Color[]): Color | null => {
  const boardIds = configuration.configurationInstallations
    .filter(i => i.configurationBoards.some(b => b.boardId))
    .map(i => i.configurationBoards
      .filter(b => b.boardId)
      .map(b => b.boardId))
    .flat();

  // Pas de lame sélectionnée
  if (boardIds.length === 0) {
    return null;
  }

  const configurationBoards = colors.filter(b => boardIds.includes(b.id));

  const maxWidth = Math.max(...configurationBoards.map(b => b.width));

  return configurationBoards.find(b => b.width === maxWidth);
};

/**
 * Récupère la longueur maximale des lames utilisées par la configuration
 * @param configuration
 */
const getConfigurationBoardsMaxLength = (configuration: Configuration): number => {
  const boardWidths = configuration.configurationInstallations
    .map(i => i.configurationBoards
      .map(b => b.boardWidth))
    .flat();
  return boardWidths.length > 0 ? Math.max(...boardWidths) : 0;
}

/**
 * Récupère la hauteur maximale des lames utilisées par la configuration
 * @param configuration
 */
const getConfigurationBoardsMaxHeight = (configuration: Configuration): number => {
  const boardHeights = configuration.configurationInstallations
    .map(i => i.configurationBoards
      .map(b => b.boardHeight))
    .flat();
  return boardHeights.length > 0 ? Math.max(...boardHeights) : 0;
}

/**
 * Calcule la longueur des décors par longueur.
 * Les côtés avec une hauteur inférieure à 183cm ne peuvent pas avoir de décor.
 * Les décors sont donc répartie de manière égale entre chaque longueur faisant plus de 183cm.
 * @param side
 * @param section
 */
const getVerticalDecorsLengthByConfigurationSideLength = (side: Side, section: Section): number => {
  // pas de décor vertical pour les sections avec une hauteur en dessous de 1m83cm
  if (section.height < 183) {
    return 0;
  }

  const sectionIndex = side.sections.indexOf(section);

  const decorativeBoardsQuantity = side.verticalDecorativeBoards.map(d => d.quantity).reduce((a, b) => a + b, 0);
  const decorativeBoardLength = side.verticalDecorativeBoards[0]?.height ?? 0;
  const decorsQuantity = side.verticalDecorations.map(d => d.quantity).reduce((a, b) => a + b, 0);
  const decorLength = side.verticalDecorations[0]?.height ?? 0;
  const panelsQuantity = side.verticalPanels.map(d => d.quantity).reduce((a, b) => a + b, 0);
  const panelLength = side.verticalPanels[0]?.height ?? 0;

  // Nombre de longueurs avec la possibilité d'accueillir des décors
  const nbLengthWithDecors = side.sections.filter(s => s.height >= 183).length;

  // Calcul de la longueur des décors
  let decorsLength =
    Math.floor(decorativeBoardsQuantity / nbLengthWithDecors) * decorativeBoardLength +
    Math.floor(decorsQuantity / nbLengthWithDecors) * decorLength +
    Math.floor(panelsQuantity / nbLengthWithDecors) * panelLength;

  if (sectionIndex === 0) {
    const decorativeBoardSurplus = decorativeBoardsQuantity % nbLengthWithDecors;
    const decorSurplus = decorsQuantity % nbLengthWithDecors;
    const panelSurplus = panelsQuantity % nbLengthWithDecors;

    decorsLength += decorativeBoardSurplus * decorativeBoardLength + decorSurplus * decorLength + panelSurplus * panelLength;
  }

  return decorsLength;
}

/**
 * Retourne le nombre de portillon(s) restant(s) à sélectionner pour une configuration
 * @param configuration la configuration
 * @returns le nombre de portillon(s) restant(s) à sélectionner
 */
const doorNumber = (configuration: Configuration): number => {
  if (!configuration.sides) {
    return 0;
  }

  let res = configuration.sides.map(s => s.sections.filter(ss => ss.hasDoor).length);

  if (res.length <= 0) {
    return 0;
  }

  return res.reduce((max, d) => max + d, 0);
}

export const remainingDoorNumber = (configuration: Configuration): number => {
  let maxDoorsRemaining = doorNumber(configuration);

  if (configuration.doors) {
    maxDoorsRemaining -= configuration.doors.map(d => d.quantity).reduce((max, d) => max + d, 0);
  }

  return maxDoorsRemaining;
}
